import React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { validateToken } from "../../utilities/userAuthentication";
import apiClient from "../../axiosInstance";

const PERMISSION_CACHE_KEY = "userPermissions";

const featureFromPath = (pathname) => {
  if (!pathname) return null;

  if (pathname === "/dashboard") return "dashboard";

  if (
    pathname === "/users" ||
    pathname === "/createUser" ||
    /^\/users\/[^/]+$/.test(pathname)
  ) {
    return "users";
  }

  if (pathname === "/roles" || pathname === "/createRole" || pathname === "/rolePermissions") {
    return "roles";
  }

  if (pathname === "/permissions") return "permissions";
  if (pathname === "/changePassword") return "users";

  if (
    pathname === "/branches" ||
    pathname === "/createBranch" ||
    pathname.startsWith("/updateBranches/")
  ) {
    return "branches";
  }

  if (
    pathname === "/owners" ||
    pathname === "/createOwner" ||
    /^\/owners\/[^/]+$/.test(pathname)
  ) {
    return "owners";
  }

  if (
    pathname === "/expenseTypes" ||
    pathname === "/createExpenseType" ||
    /^\/expenseTypes\/[^/]+$/.test(pathname)
  ) {
    return "expense_types";
  }

  if (
    pathname === "/expenses" ||
    pathname === "/createExpense" ||
    /^\/expenses\/[^/]+$/.test(pathname)
  ) {
    return "expenses";
  }

  if (pathname === "/cashFlowReport") return "cash_flow_report";
  if (pathname === "/vehicleModels") return "vehicle_models";
  if (pathname === "/vehicleMakes") return "vehicle_makes";

  if (
    pathname === "/vehiclesList" ||
    pathname === "/createVehicle" ||
    pathname.startsWith("/updateVehicle/")
  ) {
    return "vehicles";
  }

  if (
    pathname === "/vehicleRegistrations" ||
    pathname === "/registrationsList" ||
    pathname.startsWith("/updateRegistration/")
  ) {
    return "vehicle_registrations";
  }

  if (pathname === "/registrationFees") return "registration_fees";
  if (pathname === "/paymentMethods") return "payment_methods";
  if (pathname === "/paymentAccounts") return "payment_accounts";
  if (pathname === "/charges") return "charges";
  if (pathname === "/payments") return "payments";
  if (pathname === "/inspectionItems") return "inspection_items";

  if (
    pathname === "/vehicleInspections" ||
    pathname === "/inspectVehicle" ||
    pathname.startsWith("/updateInspectVehicle/")
  ) {
    return "vehicle_inspections";
  }

  return null;
};

const modeActionFromPath = (pathname) => {
  if (!pathname) return null;

  const createRoutes = [
    "/createUser",
    "/createRole",
    "/createBranch",
    "/createOwner",
    "/createExpenseType",
    "/createExpense",
    "/createVehicle",
    "/inspectVehicle",
  ];

  if (createRoutes.includes(pathname)) {
    return "create";
  }

  if (
    pathname.startsWith("/update") ||
    /^\/users\/[^/]+$/.test(pathname) ||
    /^\/owners\/[^/]+$/.test(pathname) ||
    /^\/expenses\/[^/]+$/.test(pathname) ||
    /^\/expenseTypes\/[^/]+$/.test(pathname)
  ) {
    return "update";
  }

  return null;
};

const getElementAction = (element, pageModeAction) => {
  const tagName = element.tagName?.toLowerCase();
  if (tagName !== "button" && tagName !== "a") {
    return null;
  }

  const text = (element.textContent || "").trim().toLowerCase();
  const href = (element.getAttribute("href") || "").toLowerCase();
  const iconClasses = (
    element.querySelector("i")?.className?.toLowerCase() || ""
  ).toLowerCase();

  const deleteMatch =
    /delete|remove/.test(text) ||
    iconClasses.includes("delete") ||
    iconClasses.includes("close");
  if (deleteMatch) return "delete";

  const updateMatch =
    /edit|update/.test(text) ||
    href.includes("/update") ||
    iconClasses.includes("edit");
  if (updateMatch) return "update";

  const createMatch =
    /create|add new|add/.test(text) ||
    href.includes("/create") ||
    iconClasses.includes("add");
  if (createMatch) return "create";

  if (/^save$/i.test(text) || text === "save") {
    return pageModeAction;
  }

  return null;
};

const RouteWrapper = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [permissionCodes, setPermissionCodes] = useState([]);
  const observerRef = useRef(null);

  const authUser = useMemo(
    () => JSON.parse(localStorage.getItem("authUser") || "null"),
    [],
  );

  const isAdmin = String(authUser?.user?.role || "")
    .trim()
    .toLowerCase() === "admin";

  const getHasPermission = (feature, action) => {
    if (!feature || !action) return true;
    if (isAdmin) return true;
    return permissionCodes.includes(`${feature}.${action}`);
  };

  const applyPermissionVisibility = () => {
    const feature = featureFromPath(location.pathname);
    const pageModeAction = modeActionFromPath(location.pathname);

    document.querySelectorAll('[data-rbac-hidden="1"]').forEach((element) => {
      element.style.removeProperty("display");
      element.removeAttribute("data-rbac-hidden");
    });

    if (!feature) {
      return;
    }

    const candidates = document.querySelectorAll(
      "button, a.btn, a[role='button'], a[href]",
    );

    candidates.forEach((element) => {
      const action = getElementAction(element, pageModeAction);
      if (!action) return;

      if (!getHasPermission(feature, action)) {
        element.style.display = "none";
        element.setAttribute("data-rbac-hidden", "1");
      }
    });
  };

  const loadPermissions = async () => {
    if (isAdmin) {
      setPermissionCodes(["*"]);
      return;
    }

    try {
      const cached = JSON.parse(
        localStorage.getItem(PERMISSION_CACHE_KEY) || "null",
      );
      if (Array.isArray(cached)) {
        setPermissionCodes(cached);
      }

      const result = await apiClient.get("/permissions/myPermissions");
      const codes = Array.isArray(result?.data?.response)
        ? result.data.response
        : [];

      setPermissionCodes(codes);
      localStorage.setItem(PERMISSION_CACHE_KEY, JSON.stringify(codes));
    } catch (error) {
      console.error("Failed to load user permissions:", error);
      setPermissionCodes([]);
      localStorage.setItem(PERMISSION_CACHE_KEY, JSON.stringify([]));
    }
  };

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem("authUser"))?.token;
    const isValidToken = validateToken(token);
    if (!isValidToken) {
      localStorage.removeItem("authUser");
      localStorage.removeItem(PERMISSION_CACHE_KEY);
      navigate("/login"); // Redirect to login if the token is invalid
    }
    const pathSegments = location.pathname.split("/").filter(Boolean); // Remove empty parts
    if (pathSegments.length > 0) {
      const pageName = pathSegments[0]; // Get first segment (e.g., "employees" or "createUser")
      const formattedName = pageName
        .replace(/([a-z])([A-Z])/g, "$1 $2") // "vehicleModels" → "vehicle Models"
        .split(" ") // ["vehicle", "Models"]
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // ["Vehicle", "Models"]
        .join(" "); // "Vehicle Models"
      document.title = formattedName;
    } else {
      document.title = ""; // Default title
    }
  }, [location.pathname, navigate]); // Added `navigate` to the dependency array

  useEffect(() => {
    loadPermissions();
  }, []);

  useEffect(() => {
    applyPermissionVisibility();

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new MutationObserver(() => {
      applyPermissionVisibility();
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [location.pathname, permissionCodes]);

  return <>{children}</>;
};

export default RouteWrapper;
